import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BookA, Languages, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../context/ChatContext';
import './SelectionPopover.css';

export default function SelectionPopover({ containerRef }) {
  const { t } = useTranslation('chat');
  const { lookupDictionary, lookupGrammar, lookupNative, sending } = useChat();
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState('');
  const popoverRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Check selection and show popover (works on all platforms)
  const checkSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (!text || text.length === 0) {
      setShow(false);
      return;
    }

    // Check if selection is within our container
    if (!containerRef?.current?.contains(selection.anchorNode)) {
      setShow(false);
      return;
    }

    // Get selection bounds (viewport coordinates)
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    // Position BELOW selection (avoids conflict with system selection UI on mobile)
    const x = rect.left + rect.width / 2;
    const y = rect.bottom + 8;

    setSelectedText(text);
    setPosition({ x, y });
    setShow(true);
  }, [containerRef]);

  // Listen for selectionchange - works on all platforms (desktop, iOS, Android)
  useEffect(() => {
    const handleSelectionChange = () => {
      // Debounce: wait for selection to settle (especially important on mobile)
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(checkSelection, 300);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      clearTimeout(debounceTimerRef.current);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [checkSelection]);

  // Also listen for touchend as backup for mobile
  useEffect(() => {
    const handleTouchEnd = () => {
      // Delay to let selection settle after touch
      setTimeout(checkSelection, 350);
    };

    document.addEventListener('touchend', handleTouchEnd);
    return () => document.removeEventListener('touchend', handleTouchEnd);
  }, [checkSelection]);

  // Handle click/touch outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Adjust popover position to prevent viewport overflow (like Reedle)
  useEffect(() => {
    if (!popoverRef.current || !show) return;

    const popoverRect = popoverRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Calculate ideal centered position
    const idealLeft = position.x;

    // Clamp to viewport bounds (with 8px padding on each side)
    const minLeft = popoverRect.width / 2 + 8;
    const maxLeft = viewportWidth - popoverRect.width / 2 - 8;
    const clampedLeft = Math.max(minLeft, Math.min(maxLeft, idealLeft));

    // Only update if position needs adjustment
    if (Math.abs(clampedLeft - idealLeft) > 1) {
      popoverRef.current.style.left = `${clampedLeft}px`;
    }
  }, [show, position]);

  const handleDictionary = () => {
    if (!selectedText || sending) return;
    lookupDictionary(selectedText);
    setShow(false);
    window.getSelection()?.removeAllRanges();
  };

  const handleGrammar = () => {
    if (!selectedText || sending) return;
    lookupGrammar(selectedText);
    setShow(false);
    window.getSelection()?.removeAllRanges();
  };

  const handleNative = () => {
    if (!selectedText || sending) return;
    lookupNative(selectedText);
    setShow(false);
    window.getSelection()?.removeAllRanges();
  };

  if (!show) return null;

  // Render via portal to avoid clipping by parent containers
  return createPortal(
    <div
      ref={popoverRef}
      className="selection-popover"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        transform: 'translateX(-50%)', // Center horizontally, positioned below selection
        zIndex: 9999,
      }}
    >
      <button
        className="popover-btn dict-btn"
        onClick={handleDictionary}
        disabled={sending}
        title={t('selection.dictTooltip')}
      >
        <BookA size={14} />
        <span>{t('selection.dict')}</span>
      </button>
      <button
        className="popover-btn grammar-btn"
        onClick={handleGrammar}
        disabled={sending}
        title={t('selection.grammarTooltip')}
      >
        <Languages size={14} />
        <span>{t('selection.grammar')}</span>
      </button>
      <button
        className="popover-btn native-btn"
        onClick={handleNative}
        disabled={sending}
        title={t('selection.nativeTooltip')}
      >
        <Sparkles size={14} />
        <span>{t('selection.native')}</span>
      </button>
    </div>,
    document.body
  );
}

